import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, MapPin, GraduationCap, Wallet, Layers, CheckCircle2, ArrowRight, Star, Building2, Award, Gift, BadgePercent, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { SectionHeading, Reveal, Eyebrow } from "../components/common";
import { UNIVERSITIES } from "../mock";
import api from "../lib/api";

export default function UniversityDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const u = UNIVERSITIES.find((x) => x.slug === slug);
  const [offers, setOffers] = useState(null);

  useEffect(() => {
    api.get(`/universities/${slug}`)
      .then((r) => setOffers(r.data))
      .catch(() => setOffers(null));
  }, [slug]);

  if (!u) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h2 className="font-display text-3xl font-bold text-[#012a1e]">University not found</h2>
        <Link to="/universities"><Button className="mt-6 bg-[#004030] text-white rounded-full px-6">Back to universities</Button></Link>
      </div>
    );
  }

  return (
    <>
      <section className="relative bg-[#004030] overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={u.image} alt={u.name} className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#004030] via-[#004030]/92 to-[#004030]/55" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6">
            <ArrowLeft size={16} /> Back
          </button>
          {u.featured && (
            <span className="inline-flex items-center gap-1.5 bg-[#c9a227] text-[#013d2b] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide mb-4">
              <Star size={13} className="fill-[#013d2b]" /> Featured Partner University
            </span>
          )}
          <div className="flex items-start gap-5">
            {u.logo && (
              <div className="hidden sm:grid place-items-center w-24 h-24 rounded-2xl bg-white p-3 shrink-0 shadow-xl">
                <img src={u.logo} alt={u.name} className="max-w-full max-h-full object-contain" />
              </div>
            )}
            <div>
              <h1 className="font-display font-bold text-white text-3xl md:text-5xl leading-tight">{u.name}</h1>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-white/80">
                <span className="flex items-center gap-1.5"><MapPin size={16} className="text-[#e0c05a]" /> {u.city}, {u.country} {u.flag}</span>
                <span className="flex items-center gap-1.5"><Star size={16} className="text-[#e0c05a]" /> {u.rank}</span>
              </div>
            </div>
          </div>
          <p className="mt-6 text-white/75 text-lg max-w-3xl leading-relaxed">{u.intro}</p>

          <div className="flex flex-wrap gap-4 mt-8">
            {u.website && (
              <a href={u.website} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] rounded-full h-12 px-7 font-bold shadow-lg">
                  Visit Official Website <ExternalLink size={17} className="ml-1" />
                </Button>
              </a>
            )}
            <Link to="/contact">
              <Button variant="outline" className="rounded-full h-12 px-7 font-semibold border-white/30 bg-white/5 text-white hover:bg-white hover:text-[#004030]">
                Apply with GDIH <ArrowRight size={17} className="ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quick facts */}
      <section className="bg-white -mt-10 relative z-10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 bg-white rounded-2xl shadow-xl border border-slate-100 p-6 md:p-8">
            {[
              { icon: Wallet, label: "Annual Tuition", value: u.tuition },
              { icon: Layers, label: "Study Levels", value: u.levels.join(", ") },
              { icon: Building2, label: "Campuses", value: `${u.campuses.length} campus${u.campuses.length > 1 ? "es" : ""}` },
            ].map((f) => (
              <div key={f.label} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#f0f6f3] grid place-items-center text-[#004030] shrink-0"><f.icon size={22} /></div>
                <div>
                  <p className="text-[#012a1e] font-bold leading-tight">{f.value}</p>
                  <p className="text-slate-500 text-xs">{f.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scholarships & offers (from CRM, if this partner is managed there) */}
      {offers && (offers.application_fee_waiver || offers.early_bird_offer || (offers.scholarships || []).length > 0) && (
        <section className="section-pad bg-[#f7faf8]" data-testid="uni-public-offers">
          <div className="max-w-6xl mx-auto px-6">
            <SectionHeading center={false} eyebrow="Scholarships & Offers" title="Funding and incentives available" />
            <div className="grid md:grid-cols-2 gap-5 mt-8">
              {offers.application_fee_waiver && (
                <div className="bg-white rounded-2xl border border-emerald-200 p-5 flex items-start gap-3" data-testid="public-fee-waiver">
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 grid place-items-center text-emerald-600 shrink-0"><BadgePercent size={22} /></div>
                  <div><p className="font-display font-bold text-[#012a1e]">Application fee waiver</p><p className="text-sm text-slate-600 mt-0.5">{offers.application_fee_waiver_note || "Application fee waived for eligible applicants."}</p></div>
                </div>
              )}
              {offers.early_bird_offer && (
                <div className="bg-white rounded-2xl border border-amber-200 p-5 flex items-start gap-3" data-testid="public-early-bird">
                  <div className="w-11 h-11 rounded-xl bg-amber-50 grid place-items-center text-amber-600 shrink-0"><Clock size={22} /></div>
                  <div><p className="font-display font-bold text-[#012a1e]">Early-bird incentive</p><p className="text-sm text-slate-600 mt-0.5">{offers.early_bird_offer}{offers.early_bird_deadline ? ` · by ${offers.early_bird_deadline}` : ""}</p></div>
                </div>
              )}
              {(offers.scholarships || []).map((s, i) => (
                <div key={s.id || i} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-start gap-3" data-testid={`public-scholarship-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-[#f0f6f3] grid place-items-center text-[#004030] shrink-0"><Award size={22} /></div>
                  <div>
                    <p className="font-display font-bold text-[#012a1e]">{s.name}</p>
                    <p className="text-sm text-slate-600 mt-0.5">{[s.coverage, s.intake, s.deadline ? `apply by ${s.deadline}` : ""].filter(Boolean).join(" · ")}</p>
                    {s.eligibility && <p className="text-xs text-slate-500 mt-1">{s.eligibility}</p>}
                    {s.link && <a href={s.link} target="_blank" rel="noreferrer" className="text-xs text-[#004030] underline mt-1 inline-block">More details</a>}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-5">Offers are set by the university and may change. Speak to a GDIH counsellor to confirm eligibility.</p>
          </div>
        </section>
      )}

      {/* Programmes */}
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14">
          <div>
            <SectionHeading center={false} eyebrow="Programmes" title="Popular fields of study" />
            <div className="grid sm:grid-cols-2 gap-3 mt-8">
              {u.programmes.map((p) => (
                <div key={p} className="flex items-center gap-3 bg-[#f7faf8] rounded-xl px-4 py-3.5 border border-slate-100">
                  <GraduationCap size={18} className="text-[#c9a227] shrink-0" />
                  <span className="text-slate-700 text-sm font-medium">{p}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <SectionHeading center={false} eyebrow="Campuses & Highlights" title="Where you'll study" />
            <div className="space-y-3 mt-8">
              {u.campuses.map((c) => (
                <div key={c} className="flex items-center gap-3 text-slate-700">
                  <MapPin size={18} className="text-[#004030]" /> {c} Campus
                </div>
              ))}
            </div>
            <div className="mt-6 space-y-3">
              {u.tags.map((t) => (
                <div key={t} className="flex items-center gap-3 text-slate-600">
                  <CheckCircle2 size={18} className="text-[#c9a227]" /> {t}
                </div>
              ))}
            </div>
            <div className="mt-8 bg-[#004030] rounded-2xl p-6 text-white">
              <p className="font-display text-xl font-bold">Interested in {u.name}?</p>
              <p className="text-white/70 text-sm mt-1">Our counsellors will guide your application end-to-end.</p>
              <Link to="/contact"><Button className="mt-4 bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] rounded-full h-11 px-6 font-bold">Talk to a counsellor</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}